import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Heading, Panel } from '@savgroup-front-common/core';

import BulkAction from '../components/BulkAction';
import DossierBulkActionDrawer from '../components/DossierBulkActionDrawer';
import DossierCardList from '../components/DossierCardList';
import DossierStatusFilter from '../components/DossierStatusFilter';
import DossierTableView from '../components/DossierTableView';
import { useMobileGlobalSearch } from '../context/MobileGlobalSearchContext';
import { MOCK_DOSSIERS } from '../data/dossierMock';
import { filterDossiersBySearchQuery, sortDossiersByLastModified } from '../utils/dossierSearch';
import {
  filterDossiersByStatut,
  getDossierStatutFilterOptions,
} from '../utils/dossierStatut';
import { DossierSelectionProvider, useDossierSelection } from './DossierPage.context';
import { $Page, $PanelInner, DOSSIER_RESPONSIVE_MAX_WIDTH_PX } from './DossierPage.styles';

const DossierPageContent = () => {
  const { selectedIds, deselectAll } = useDossierSelection();
  const { searchQuery } = useMobileGlobalSearch();
  const [isBulkDrawerOpen, setIsBulkDrawerOpen] = useState(false);
  const [selectedStatut, setSelectedStatut] = useState<string | undefined>();
  const [isNarrowViewport, setIsNarrowViewport] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia(`(max-width: ${DOSSIER_RESPONSIVE_MAX_WIDTH_PX}px)`).matches
      : false,
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const mediaQuery = window.matchMedia(`(max-width: ${DOSSIER_RESPONSIVE_MAX_WIDTH_PX}px)`);
    const onChange = () => {
      setIsNarrowViewport(mediaQuery.matches);
    };

    onChange();
    mediaQuery.addEventListener('change', onChange);

    return () => mediaQuery.removeEventListener('change', onChange);
  }, []);

  const statutFilterOptions = useMemo(
    () => getDossierStatutFilterOptions(MOCK_DOSSIERS),
    [],
  );

  const dossiers = useMemo(() => {
    const sortedDossiers = sortDossiersByLastModified(MOCK_DOSSIERS);

    if (!isNarrowViewport) {
      return sortedDossiers;
    }

    const searchFilteredDossiers = filterDossiersBySearchQuery(sortedDossiers, searchQuery);

    return filterDossiersByStatut(searchFilteredDossiers, selectedStatut);
  }, [isNarrowViewport, searchQuery, selectedStatut]);

  const handleSelectStatut = useCallback((statut: string | undefined) => {
    setSelectedStatut(statut);
  }, []);

  const handleBulkAction = useCallback(() => {
    setIsBulkDrawerOpen(true);
  }, []);

  const handleCloseBulkDrawer = useCallback(() => {
    setIsBulkDrawerOpen(false);
  }, []);

  const hasBulkSelection = !isNarrowViewport && selectedIds.size > 0;

  useEffect(() => {
    if (!isNarrowViewport) {
      return;
    }

    deselectAll();
    setIsBulkDrawerOpen(false);
    setSelectedStatut(undefined);
  }, [isNarrowViewport, deselectAll]);

  const listContent = (
    <$PanelInner>
      <Heading level={2}>Dossiers</Heading>
      {isNarrowViewport ? (
        <>
          <DossierStatusFilter
            options={statutFilterOptions}
            selectedStatut={selectedStatut}
            onSelectStatut={handleSelectStatut}
          />
          <DossierCardList
            dossiers={dossiers}
            searchQuery={searchQuery}
            statusFilter={selectedStatut}
          />
        </>
      ) : (
        <DossierTableView dossiers={dossiers} />
      )}
      {hasBulkSelection && (
        <BulkAction
          count={selectedIds.size}
          deselectAll={deselectAll}
          buttonMessage={{
            id: 'dossier.bulkAction',
            defaultMessage: 'Suivant',
          }}
          onClick={handleBulkAction}
          selectedLabel={`${selectedIds.size} dossier(s) sélectionné(s)`}
        />
      )}
    </$PanelInner>
  );

  return (
    <$Page $hasBulkAction={hasBulkSelection}>
      {isNarrowViewport ? listContent : <Panel>{listContent}</Panel>}
      <DossierBulkActionDrawer
        isOpen={!isNarrowViewport && isBulkDrawerOpen}
        onClose={handleCloseBulkDrawer}
        selectedCount={selectedIds.size}
      />
    </$Page>
  );
};

const DossierPage = () => {
  const allDossierIds = useMemo(
    () => MOCK_DOSSIERS.map((dossier) => dossier.numeroDossier),
    [],
  );

  return (
    <DossierSelectionProvider allDossierIds={allDossierIds}>
      <DossierPageContent />
    </DossierSelectionProvider>
  );
};

export default DossierPage;
